import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PageHeader, Card, Tag, fmt } from "../components/UI";
import DataTable from "../components/DataTable";
import { Users, Layers, Rocket, BarChart3, Loader2, X, Search, AlertOctagon, MapPin, ShieldCheck } from "lucide-react";
import api, { formatError } from "../lib/api";

const TONE_BG = {
  emerald: "bg-emerald-50 border-emerald-200 text-emerald-800",
  teal:    "bg-teal-50 border-teal-200 text-teal-800",
  navy:    "bg-slate-900/5 border-slate-300 text-slate-900",
  amber:   "bg-amber-50 border-amber-200 text-amber-800",
  rose:    "bg-rose-50 border-rose-200 text-rose-800",
  slate:   "bg-slate-50 border-slate-200 text-slate-700",
};

function GroupBar({ groups, value, onChange }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {groups.map((g) => (
        <button key={g} onClick={() => onChange(g)} data-testid={`group-${g}`}
          className={`text-[12px] px-3 py-1 rounded-full border transition ${value === g ? "bg-brand-navy text-white border-brand-navy" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
          {g}
        </button>
      ))}
    </div>
  );
}

function CohortCard({ c, onOpen, onActivate }) {
  const tone = TONE_BG[c.tone] || TONE_BG.slate;
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4 flex flex-col gap-2 surface-hover" data-testid={`cohort-${c.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className={`tag ${tone} !text-[10px] uppercase tracking-wider`}>{c.group}</div>
        {c.stub && <Tag tone="amber">Awaiting feed</Tag>}
      </div>
      <h3 className="text-[14px] font-medium text-slate-900 leading-snug">{c.label}</h3>
      <p className="text-[11px] text-slate-500 leading-relaxed line-clamp-3">{c.desc}</p>
      <div className="mt-auto pt-3 flex items-end justify-between">
        <div>
          <div className="serif-display text-3xl numerals text-slate-900">{fmt(c.count || 0)}</div>
          <div className="text-[10px] small-caps text-slate-400">members</div>
        </div>
        <div className="flex flex-col gap-1.5">
          <button onClick={() => onOpen(c)} className="text-[11px] text-slate-600 hover:text-slate-900 inline-flex items-center gap-1" data-testid={`open-${c.id}`}>
            <BarChart3 size={11} /> Drill down
          </button>
          <button onClick={() => onActivate(c)} disabled={c.stub || (c.count || 0) === 0}
            className="btn-primary !py-1 !px-3 text-[11px] inline-flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid={`activate-${c.id}`}>
            <Rocket size={11} /> Activate
          </button>
        </div>
      </div>
    </div>
  );
}

function DistributionBar({ rows, max }) {
  if (!rows?.length) return <div className="text-[11px] text-slate-400">No data.</div>;
  return (
    <div className="space-y-1.5">
      {rows.slice(0, 8).map((r, i) => (
        <div key={i} className="flex items-center gap-2 text-[11px]">
          <div className="w-24 truncate text-slate-700">{r.label}</div>
          <div className="flex-1 h-2 bg-slate-100 rounded overflow-hidden"><div className="h-full bg-teal-600" style={{ width: `${Math.min(100, (r.value / (max || 1)) * 100)}%` }} /></div>
          <div className="w-12 text-right text-slate-900 numerals tabular-nums">{fmt(r.value)}</div>
        </div>
      ))}
    </div>
  );
}

function DetailDrawer({ cohort, onClose, onActivate }) {
  const [detail, setDetail] = useState(null);
  const [analytics, setAnalytics] = useState(null);

  useEffect(() => { (async () => {
    if (!cohort) return;
    const [a, b] = await Promise.all([
      api.get(`/cohorts/${cohort.id}?members_limit=500`),
      api.get(`/cohorts/${cohort.id}/analytics`),
    ]);
    setDetail(a.data); setAnalytics(b.data);
  })(); }, [cohort]);

  if (!cohort) return null;
  const tone = TONE_BG[cohort.tone] || TONE_BG.slate;
  const maxCity = Math.max(...(analytics?.by_city || []).map((r) => r.value), 1);
  const maxState = Math.max(...(analytics?.by_state || []).map((r) => r.value), 1);

  const columns = [
    { key: "physio_code", label: "ERP ID", filter: "text", render: (r) => <span className="font-mono text-[11px]">{r.erp_outlet_id || r.physio_code}</span> },
    { key: "name", label: "Physio", filter: "text", accessor: (r) => r.owner_name || `${r.first_name} ${r.last_name}`,
      render: (r) => <div><div className="font-medium">{r.owner_name || `${r.first_name} ${r.last_name}`}</div><div className="text-[11px] text-slate-500">{r.clinic_name}</div></div> },
    { key: "mobile", label: "Mobile", filter: "text" },
    { key: "city", label: "City", filter: "text" },
    { key: "state", label: "State", filter: "text" },
    { key: "certified", label: "Certified", filter: "select", options: ["Yes","No"], accessor: (r) => r.certified ? "Yes" : "No",
      render: (r) => r.certified ? <Tag tone="emerald">Yes</Tag> : <Tag tone="slate">No</Tag> },
    { key: "kyc_status", label: "KYC", filter: "select", options: ["verified","pending"],
      render: (r) => <Tag tone={r.kyc_status === "verified" ? "emerald" : "amber"}>{r.kyc_status || "—"}</Tag> },
    { key: "erp_outlet_id", label: "Outlet ERP", filter: "text", render: (r) => <span className="font-mono text-[11px]">{r.erp_outlet_id || "—"}</span> },
  ];

  return (
    <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-40 flex items-stretch justify-end" onClick={onClose} data-testid="cohort-drawer">
      <div className="w-full md:w-[920px] bg-white border-l border-slate-200 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 z-10">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className={`tag ${tone} !text-[10px] uppercase tracking-wider mb-1`}>{cohort.group}</div>
              <h2 className="serif-display text-2xl text-slate-900 truncate">{cohort.label}</h2>
              <p className="text-[12px] text-slate-500 mt-1">{cohort.desc}</p>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => onActivate(cohort)} disabled={cohort.stub || (cohort.count || 0) === 0}
                className="btn-primary inline-flex items-center gap-2 disabled:opacity-50" data-testid="drawer-activate">
                <Rocket size={13} /> Activate Campaign
              </button>
              <button onClick={onClose} className="p-2 rounded hover:bg-slate-100" data-testid="drawer-close"><X size={18} /></button>
            </div>
          </div>
        </div>

        <div className="px-6 py-6 space-y-6">
          {cohort.stub && (
            <div className="tag tag-amber"><AlertOctagon size={12} /> {cohort.stub}</div>
          )}

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Kpi label="Members" value={detail?.member_count || 0} />
            <Kpi label="Active Points" value={analytics?.total_points_active || 0} />
            <Kpi label="Pending Points" value={analytics?.total_points_pending || 0} />
            <Kpi label="Avg orders / physio" value={analytics?.avg_orders || 0} />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <div className="flex items-center gap-2 mb-3"><MapPin size={13} className="text-slate-600" /><h3 className="text-[13px] font-medium">By City (top 8)</h3></div>
              {analytics ? <DistributionBar rows={analytics.by_city} max={maxCity} /> : <Loader2 size={14} className="animate-spin text-slate-400" />}
            </Card>
            <Card>
              <div className="flex items-center gap-2 mb-3"><Layers size={13} className="text-slate-600" /><h3 className="text-[13px] font-medium">By State (top 8)</h3></div>
              {analytics ? <DistributionBar rows={analytics.by_state} max={maxState} /> : <Loader2 size={14} className="animate-spin text-slate-400" />}
            </Card>
            <Card>
              <div className="flex items-center gap-2 mb-3"><ShieldCheck size={13} className="text-slate-600" /><h3 className="text-[13px] font-medium">Certification split</h3></div>
              {analytics ? <DistributionBar rows={analytics.by_certified} max={Math.max(...(analytics.by_certified || []).map((r) => r.value), 1)} /> : <Loader2 size={14} className="animate-spin text-slate-400" />}
            </Card>
            <Card>
              <div className="flex items-center gap-2 mb-3"><Users size={13} className="text-slate-600" /><h3 className="text-[13px] font-medium">KYC status</h3></div>
              {analytics ? <DistributionBar rows={analytics.by_kyc} max={Math.max(...(analytics.by_kyc || []).map((r) => r.value), 1)} /> : <Loader2 size={14} className="animate-spin text-slate-400" />}
            </Card>
          </div>

          <Card>
            <h3 className="text-[13px] font-medium mb-3">Members ({detail?.member_count || 0})</h3>
            {detail?.members?.length ? (
              <DataTable columns={columns} data={detail.members} rowKey="id"
                searchableKeys={["owner_name","clinic_name","mobile","city","state","physio_code","erp_outlet_id"]}
                exportFilename={`cohort-${cohort.id}`} testIdPrefix={`cohort-members-${cohort.id}`} />
            ) : <div className="text-center py-6 text-slate-400 text-[12px]">No members in scope yet.</div>}
          </Card>
        </div>
      </div>
    </div>
  );
}

function Kpi({ label, value }) {
  return (
    <div className="surface p-3">
      <div className="small-caps text-slate-400">{label}</div>
      <div className="serif-display text-2xl text-slate-900 numerals mt-0.5">{fmt(value)}</div>
    </div>
  );
}

function ActivateModal({ cohort, onClose, onCreated }) {
  const [name, setName] = useState(`${cohort?.label || ""} · ${new Date().toLocaleDateString()}`);
  const [channel, setChannel] = useState("whatsapp");
  const [template, setTemplate] = useState(`Hi {{first_name}}, you've been picked for our ${cohort?.label} campaign. Reply MENU on WhatsApp to claim your reward.`);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  async function submit(e) {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      const { data } = await api.post(`/cohorts/${cohort.id}/activate-campaign`, { name, channel, template });
      onCreated?.(data);
    } catch (e) { setErr(formatError(e)); }
    finally { setBusy(false); }
  }

  if (!cohort) return null;
  return (
    <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={submit} className="bg-white rounded-xl border border-slate-200 w-full max-w-lg p-6 space-y-3" onClick={(e) => e.stopPropagation()} data-testid="activate-modal">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="serif-display text-2xl">Activate Campaign</h3>
            <p className="text-[11px] text-slate-500 mt-0.5">Cohort: <span className="font-medium text-slate-700">{cohort.label}</span> · <span className="numerals">{fmt(cohort.count || 0)}</span> members</p>
          </div>
          <button type="button" onClick={onClose}><X size={18} /></button>
        </div>
        <div>
          <label className="small-caps text-slate-500">Campaign name</label>
          <input value={name} onChange={(e) => setName(e.target.value)} required data-testid="activate-name" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="small-caps text-slate-500">Channel</label>
            <select value={channel} onChange={(e) => setChannel(e.target.value)} className="w-full" data-testid="activate-channel">
              <option value="whatsapp">WhatsApp</option>
              <option value="sms">SMS</option>
              <option value="email">Email</option>
            </select>
          </div>
          <div>
            <label className="small-caps text-slate-500">Audience size</label>
            <div className="px-3 py-2 bg-slate-50 rounded-md text-[13px] numerals">{fmt(cohort.count || 0)}</div>
          </div>
        </div>
        <div>
          <label className="small-caps text-slate-500">Message template</label>
          <textarea value={template} onChange={(e) => setTemplate(e.target.value)} rows={4} className="w-full" required data-testid="activate-template" />
        </div>
        {err && <div className="tag tag-rose" data-testid="activate-error">{err}</div>}
        <button type="submit" disabled={busy} className="btn-primary w-full inline-flex items-center justify-center gap-2" data-testid="activate-submit">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Rocket size={14} />}
          {busy ? "Creating…" : "Create campaign & queue audience"}
        </button>
      </form>
    </div>
  );
}

export default function Cohorts() {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [group, setGroup] = useState("All");
  const [openCohort, setOpenCohort] = useState(null);
  const [activateCohort, setActivateCohort] = useState(null);
  const nav = useNavigate();

  async function fetchCohorts() {
    const { data } = await api.get("/cohorts");
    setItems(data);
  }
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/cohorts");
        if (alive) setItems(data);
      } catch { /* swallow */ }
    })();
    return () => { alive = false; };
  }, []);

  const groups = useMemo(() => ["All", ...Array.from(new Set(items.map((i) => i.group)))], [items]);

  const filtered = items.filter((c) =>
    (group === "All" || c.group === group) &&
    (!search || c.label.toLowerCase().includes(search.toLowerCase()) || c.id.includes(search.toLowerCase()))
  );

  const summary = useMemo(() => ({
    cohorts: items.length,
    active: items.filter((c) => !c.stub && (c.count || 0) > 0).length,
    largest: items.reduce((m, c) => (c.count || 0) > (m.count || 0) ? c : m, {}),
  }), [items]);

  return (
    <div className="space-y-6">
      <PageHeader
        kicker="Campaign Manager"
        title="Cohort Dashboard"
        subtitle="Pre-computed segments based on the ZIXA physio segmentation tag schema (v1.1). Drill into any cohort for analytics, then activate as a campaign in one click."
        testId="cohorts-page"
        actions={
          <button onClick={() => nav("/campaigns")} className="btn-teal inline-flex items-center gap-2 !py-2" data-testid="open-campaigns-btn">
            <Rocket size={14} /> Open Campaign Manager
          </button>
        }
      />

      <div className="grid grid-cols-3 gap-3">
        <Kpi label="Total cohorts" value={summary.cohorts} />
        <Kpi label="Live (with members)" value={summary.active} />
        <div className="surface p-3">
          <div className="small-caps text-slate-400">Largest cohort</div>
          <div className="font-medium text-slate-900 mt-1 truncate">{summary.largest?.label || "—"}</div>
          <div className="text-[11px] text-slate-500 mt-0.5 numerals">{fmt(summary.largest?.count || 0)} members</div>
        </div>
      </div>

      <Card>
        <div className="flex flex-col md:flex-row md:items-center gap-3 mb-4">
          <GroupBar groups={groups} value={group} onChange={setGroup} />
          <div className="relative md:ml-auto">
            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search cohorts…"
              className="pl-8 pr-3 py-1.5 text-[12px] w-64" data-testid="cohort-search" />
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filtered.map((c) => <CohortCard key={c.id} c={c} onOpen={setOpenCohort} onActivate={setActivateCohort} />)}
          {filtered.length === 0 && <div className="col-span-full text-center py-10 text-slate-400 text-[13px]">No cohorts match your filters.</div>}
        </div>
      </Card>

      {openCohort && <DetailDrawer cohort={openCohort} onClose={() => setOpenCohort(null)} onActivate={(c) => { setOpenCohort(null); setActivateCohort(c); }} />}
      {activateCohort && (
        <ActivateModal cohort={activateCohort} onClose={() => setActivateCohort(null)}
          onCreated={(camp) => { setActivateCohort(null); alert(`Campaign created: ${camp.name}`); nav("/campaigns"); }} />
      )}
    </div>
  );
}
